import { CloseIcon, MenuIcon } from "@talladega/icons";
import LocaleButton from "@common/components/LocaleButton";
import Logo from "@common/components/Logo";
import useClientSide from "@common/hooks/useClientSide";
import classNames from "classnames";
import Link from "next/link";
import { Dispatch, SetStateAction, useState } from "react";
import { createPortal } from "react-dom";
import { User } from "@modules/user/types/user";
import { LoginModalMode, loginMode } from "@modules/user/types/LoginModalMode";
import { useTranslation } from "next-i18next";
import { TranslationKeys } from "src/@types/i18next";
import UserMenu from "@modules/user/components/UserMenu";

const navLinks: { href: string; key: TranslationKeys["common"] }[] = [
  { href: "/", key: "common:home" },
  { href: "/about", key: "common:why_throttle" },
  { href: "/faq", key: "common:faqs" },
  { href: "/contact", key: "common:contact_us" },
];

type MobileNavBarProps = {
  isMenuOpen: boolean;
  onMenuOpenChange?: (isOpen: boolean) => void;
  onLoginClick?: () => void;
};

const MobileNavBar = ({
  isMenuOpen,
  onMenuOpenChange,
  onLoginClick,
}: MobileNavBarProps) => {
  const { t } = useTranslation(["common"]);
  const isClient = useClientSide();

  return isClient
    ? createPortal(
        <div
          className={classNames(
            "fixed top-[88px] h-[calc(100vh-96px)] z-10 inset-x-0 mt-2 bg-primary-950 lg:hidden overflow-y-auto",
            {
              hidden: !isMenuOpen,
            }
          )}
        >
          <div className="h-full border-t border-white/10 mx-4">
            <div className="h-full flex flex-col justify-between gap-8">
              <div className="flex flex-col space-y-8 mt-14">
                {navLinks.map(({ href, key }) => (
                  <Link
                    key={href}
                    href={href}
                    className="text-white hover:text-white/70"
                    onClick={() => onMenuOpenChange?.(false)}
                  >
                    {t(key)}
                  </Link>
                ))}
              </div>
              <div className="flex flex-col items-center gap-4 mb-8">
                <Link
                  href="/"
                  className="w-full bg-brand-solid text-white text-center hover:bg-primary-600 rounded-full border-none px-4 py-2"
                  onClick={() => onMenuOpenChange?.(false)}
                >
                  {t("common:get_started")}
                </Link>
                <Link
                  href="#"
                  className="w-full text-white text-center hover:text-white/80 mb-2"
                  onClick={onLoginClick}
                >
                  {t("common:login")}
                </Link>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )
    : null;
};

type Props = {
  setShowLoginModal: Dispatch<SetStateAction<boolean>>;
  setLoginModalMode?: Dispatch<SetStateAction<LoginModalMode>>;
  user: User | undefined;
};

const NavBar = ({ setShowLoginModal, setLoginModalMode, user }: Props) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { t } = useTranslation(["common"]);
  const onLoginClick = () => {
    setIsMenuOpen(false);
    setLoginModalMode?.(loginMode);
    setShowLoginModal(true);
  };

  return (
    <nav className="sticky top-0 z-10 bg-primary-950 py-2">
      <div className="flex items-center justify-between mx-6 lg:mx-14 xl:mx-32">
        <Link href="/">
          <Logo className="w-36 h-20 text-white hover:text-primary-100" />
        </Link>

        {/* Desktop Nav */}
        <div className="font-normal hidden lg:flex items-center gap-8 leading-4">
          {navLinks.map(({ href, key }) => (
            <Link
              key={href}
              href={href}
              className="text-white hover:text-opacity-80"
            >
              {t(key)}
            </Link>
          ))}
          <LocaleButton />
          <hr className="hidden lg:block h-12 border-l-2 border-gray-400" />
          <div className="flex items-center gap-4">
            <Link
              href="/"
              className="bg-white text-primary hover:bg-white/80 rounded-full border border-primary-200 px-4 py-2"
            >
              {t("common:get_started")}
            </Link>
            {user && user.isLoggedIn ? (
              <UserMenu user={user} />
            ) : (
              <Link
                href="#"
                onClick={onLoginClick}
                className="text-white hover:text-white/80"
              >
                {t("common:login")}
              </Link>
            )}
          </div>
        </div>

        {/* Mobile Nav */}
        <div className="flex items-center space-x-4 lg:hidden">
          <LocaleButton />
          {user && user.isLoggedIn && (
            <UserMenu
              user={user}
              size="md"
              onTrigger={() => setIsMenuOpen(false)}
            />
          )}
          <button
            type="button"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label="Toggle Menu"
          >
            {isMenuOpen ? (
              <CloseIcon className="h-6 w-6 text-white" />
            ) : (
              <MenuIcon className="h-6 w-6 text-white" />
            )}
          </button>
        </div>
      </div>

      <MobileNavBar
        isMenuOpen={isMenuOpen}
        onMenuOpenChange={setIsMenuOpen}
        onLoginClick={onLoginClick}
      />
    </nav>
  );
};

export default NavBar;
